import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { History } from "lucide-react";
import React, { useMemo, useState } from "react";

interface FinancialHistoryEntry {
  id: string;
  effectiveDate: string;
  eventType: string;
  valueChange: number;
  bookValueAtEnd: number;
  notes: string;
}

export const HistoryTab: React.FC<{ data: any }> = ({ data }) => {
  const [filters, setFilters] = useState({
    search: "",
    eventType: "all-events",
    timeframe: "all-years",
    valueType: "all-changes",
    page: 1,
  });
  const PAGE_SIZE = 10;

  const columns: TableColumn<FinancialHistoryEntry>[] = useMemo(
    () => [
      {
        key: "effectiveDate",
        header: "Effective Date",
        type: "text",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "eventType",
        header: "Event Type",
        type: "text",
      },
      {
        key: "valueChange",
        header: "Value Change",
        type: "custom",
        render: (value: number) => (
          <span
            className={`font-medium ${
              value < 0 ? "text-red-600" : "text-green-600"
            }`}
          >
            {value < 0 ? "-" : "+"}${Math?.abs(value)}
          </span>
        ),
      },
      {
        key: "bookValueAtEnd",
        header: "Book Value at End",
        type: "custom",
        render: (value: number) => (
          <span className="font-medium">${value}</span>
        ),
      },
      {
        key: "notes",
        header: "Notes",
        type: "text",
        render: (value) => (
          <span className="text-gray-600 max-w-xs truncate" title={value}>
            {value}
          </span>
        ),
      },
    ],
    []
  );

  // Filter financial history
  const filteredHistory = useMemo(() => {
    return data?.financeData?.financialHistory.filter((entry) => {
      const matchesEventType =
        filters.eventType === "all-events" ||
        entry.eventType === filters.eventType;

      const matchesValueType =
        filters.valueType === "all-changes" ||
        (filters.valueType === "positive" && entry.valueChange > 0) ||
        (filters.valueType === "negative" && entry.valueChange < 0);

      // Simple timeframe filtering (in real app, you'd parse dates properly)
      const matchesTimeframe =
        filters.timeframe === "all-years" ||
        (filters.timeframe === "last-5" && entry.date.includes("2024")) ||
        entry.date.includes("2023") ||
        (filters.timeframe === "last-10" &&
          parseInt(entry.date.split(" ")[2]) >= 2015);

      return matchesEventType && matchesValueType && matchesTimeframe;
    });
  }, [data?.financeData?.financialHistory, filters]);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleApplyFilters = (newFilters: any) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: "",
      eventType: "all-events",
      timeframe: "all-years",
      valueType: "all-changes",
      page: 1,
    });
  };

  // Pagination logic
  const totalItems = filteredHistory?.length;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (filters.page - 1) * PAGE_SIZE;
  const paginatedHistory = filteredHistory?.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(filters.page * PAGE_SIZE, totalItems),
    }),
    [filters.page, totalPages, totalItems, startIndex]
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="page-title flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
              <History className="h-4 w-4 text-blue-600" />
            </div>
            System Financial History
          </h2>
          <p className="page-subtitle mt-1">
            Complete financial ledger showing all value changes and depreciation
            events
          </p>
        </div>
      </div>

      {/* Generic Table with integrated filter panel */}
      <GenericTable
        data={data?.financeData?.financialHistory ?? []}
        columns={columns}
        rowKey="id"
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No financial history found"
      />
    </div>
  );
};
